import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from 'recharts';
import { Skeleton } from '../components/ui/skeleton';
import { BACKEND_URL } from '../config';

const COHORT_COLORS = [
  '#3b82f6', '#ef4444', '#22c55e', '#f59e0b', '#8b5cf6',
  '#ec4899', '#14b8a6', '#f97316', '#6366f1', '#06b6d4',
  '#84cc16', '#e11d48', '#0ea5e9', '#d946ef', '#10b981',
];

const MONTHS_MAP = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
const fmtMonth = (m) => { if (!m) return ''; const [y, mo] = m.split('-'); return `${MONTHS_MAP[parseInt(mo) - 1]} '${y.slice(2)}`; };
const fmtCurrency = (v) => `₹${Number(v).toLocaleString('en-IN', { maximumFractionDigits: 0 })}`;

const CohortAnalysis = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState('retention'); // retention | revenue
  const [minSize, setMinSize] = useState(2);
  const [selectedCohorts, setSelectedCohorts] = useState(new Set());

  const fetchCohorts = useCallback(async () => {
    setLoading(true);
    try {
      const { data: res } = await axios.get(`${BACKEND_URL}/api/analytics/cohorts`, {
        params: { min_cohort_size: minSize },
        withCredentials: true,
      });
      setData(res);
      // Auto-select latest 6 cohorts for revenue chart
      const latest = (res.cohorts || []).slice(-6);
      setSelectedCohorts(new Set(latest));
    } catch (err) {
      console.error('Failed to load cohorts:', err.message);
    } finally {
      setLoading(false);
    }
  }, [minSize]);

  useEffect(() => { fetchCohorts(); }, [fetchCohorts]);

  if (loading) {
    return (
      <div className="space-y-6" data-testid="cohort-skeleton">
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          {[1,2,3,4].map(i => <div key={i} className="bg-card border border-border rounded-sm p-4 space-y-2"><Skeleton className="h-3 w-20" /><Skeleton className="h-7 w-28" /></div>)}
        </div>
        <Skeleton className="h-64 w-full rounded-sm" />
      </div>
    );
  }

  if (!data || !data.retention_matrix?.length) {
    return (
      <div className="text-center py-12 text-muted-foreground text-sm" data-testid="cohort-empty">
        Not enough data for cohort analysis. Need at least {minSize} clients per cohort month.
      </div>
    );
  }

  const { summary, retention_matrix, revenue_curves } = data;

  // Build revenue chart data (pivot: period -> {period, cohort1_rev, cohort2_rev, ...})
  const maxPeriods = Math.min(12, Math.max(...revenue_curves.filter(c => selectedCohorts.has(c.cohort)).map(c => c.points.length)));
  const revenueChartData = [];
  for (let p = 0; p < maxPeriods; p++) {
    const point = { period: `M${p}` };
    revenue_curves.filter(c => selectedCohorts.has(c.cohort)).forEach(c => {
      if (c.points[p]) {
        point[c.cohort] = c.points[p].cumulative_revenue;
      }
    });
    revenueChartData.push(point);
  }

  // Retention heatmap max periods
  const heatmapMaxPeriods = Math.min(12, Math.max(...retention_matrix.map(r => r.periods.length)));

  const getRetentionColor = (pct) => {
    if (pct >= 80) return 'bg-green-600 text-white';
    if (pct >= 60) return 'bg-green-500 text-white';
    if (pct >= 40) return 'bg-yellow-500 text-white';
    if (pct >= 20) return 'bg-orange-500 text-white';
    if (pct > 0) return 'bg-red-500 text-white';
    return 'bg-muted text-muted-foreground';
  };

  const toggleCohort = (cohort) => {
    setSelectedCohorts(prev => {
      const next = new Set(prev);
      if (next.has(cohort)) next.delete(cohort); else next.add(cohort);
      return next;
    });
  };

  return (
    <div className="space-y-6" data-testid="cohort-analysis">
      {/* Summary Cards */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <div className="bg-card border border-border rounded-sm p-4" data-testid="cohort-total-card">
          <div className="text-[10px] tracking-[0.15em] uppercase font-bold text-muted-foreground mb-1">Cohorts</div>
          <div className="text-2xl font-bold tracking-tight">{summary.total_cohorts}</div>
          <div className="text-[10px] text-muted-foreground">{summary.total_clients_in_cohorts} clients tracked</div>
        </div>
        <div className="bg-card border border-border rounded-sm p-4" data-testid="cohort-retention-card">
          <div className="text-[10px] tracking-[0.15em] uppercase font-bold text-muted-foreground mb-1">Avg M1 Retention</div>
          <div className={`text-2xl font-bold tracking-tight ${summary.avg_retention_month1 >= 50 ? 'text-green-600 dark:text-green-400' : summary.avg_retention_month1 >= 25 ? 'text-yellow-600 dark:text-yellow-400' : 'text-red-600 dark:text-red-400'}`}>
            {summary.avg_retention_month1}%
          </div>
          <div className="text-[10px] text-muted-foreground">1 month after first purchase</div>
        </div>
        <div className="bg-card border border-border rounded-sm p-4" data-testid="cohort-ltv-card">
          <div className="text-[10px] tracking-[0.15em] uppercase font-bold text-muted-foreground mb-1">Avg Lifetime Value</div>
          <div className="text-2xl font-bold tracking-tight">{fmtCurrency(summary.avg_lifetime_value)}</div>
          <div className="text-[10px] text-muted-foreground">Per client</div>
        </div>
        <div className="bg-card border border-border rounded-sm p-4" data-testid="cohort-best-card">
          <div className="text-[10px] tracking-[0.15em] uppercase font-bold text-muted-foreground mb-1">Best Cohort</div>
          <div className="text-2xl font-bold tracking-tight text-green-600 dark:text-green-400">
            {summary.best_cohort ? fmtMonth(summary.best_cohort.month) : '—'}
          </div>
          <div className="text-[10px] text-muted-foreground">{summary.best_cohort ? `${summary.best_cohort.retention}% M1 retention` : ''}</div>
        </div>
      </div>

      {/* View Toggle + Filters */}
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="flex items-center gap-2">
          <button onClick={() => setView('retention')} className={`px-3 py-1.5 text-xs rounded-sm font-medium transition-colors ${view === 'retention' ? 'bg-primary text-primary-foreground' : 'bg-card border border-border text-muted-foreground hover:text-foreground'}`} data-testid="cohort-view-retention">
            Retention Heatmap
          </button>
          <button onClick={() => setView('revenue')} className={`px-3 py-1.5 text-xs rounded-sm font-medium transition-colors ${view === 'revenue' ? 'bg-primary text-primary-foreground' : 'bg-card border border-border text-muted-foreground hover:text-foreground'}`} data-testid="cohort-view-revenue">
            Revenue Curves
          </button>
        </div>
        <div className="flex items-center gap-2">
          <label className="text-xs text-muted-foreground">Min cohort size:</label>
          <select value={minSize} onChange={(e) => setMinSize(Number(e.target.value))} className="h-7 text-xs border border-border rounded-sm bg-background px-2" data-testid="cohort-min-size">
            <option value={1}>1+</option>
            <option value={2}>2+</option>
            <option value={5}>5+</option>
            <option value={10}>10+</option>
          </select>
        </div>
      </div>

      {/* Retention Heatmap */}
      {view === 'retention' && (
        <div className="bg-card border border-border rounded-sm p-5 overflow-x-auto" data-testid="retention-heatmap">
          <div className="text-sm font-semibold tracking-tight mb-1">Client Retention by Cohort</div>
          <div className="text-[10px] text-muted-foreground mb-4">% of clients active in each month after their first purchase</div>
          <table className="w-full text-xs min-w-[600px]">
            <thead>
              <tr>
                <th className="text-left py-1.5 px-2 font-bold text-muted-foreground sticky left-0 bg-card z-10">Cohort</th>
                <th className="text-center py-1.5 px-1 font-bold text-muted-foreground w-10">Size</th>
                {Array.from({ length: heatmapMaxPeriods }).map((_, i) => (
                  <th key={i} className="text-center py-1.5 px-1 font-bold text-muted-foreground w-12">M{i}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {retention_matrix.map((row) => (
                <tr key={row.cohort} className="border-t border-border">
                  <td className="py-1 px-2 font-medium sticky left-0 bg-card z-10 whitespace-nowrap">{fmtMonth(row.cohort)}</td>
                  <td className="py-1 px-1 text-center text-muted-foreground">{row.size}</td>
                  {Array.from({ length: heatmapMaxPeriods }).map((_, i) => {
                    const period = row.periods[i];
                    if (!period) return <td key={i} className="py-1 px-1" />;
                    return (
                      <td key={i} className="py-1 px-1">
                        <div className={`rounded-sm text-center py-1 text-[10px] font-bold ${getRetentionColor(period.retention)}`} title={`${period.active} of ${row.size} active (${fmtCurrency(period.revenue)})`}>
                          {period.retention}%
                        </div>
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
          <div className="flex items-center gap-3 mt-3 text-[10px] text-muted-foreground">
            <span>Legend:</span>
            <span className="flex items-center gap-1"><span className="w-3 h-3 rounded-sm bg-green-600" />80%+</span>
            <span className="flex items-center gap-1"><span className="w-3 h-3 rounded-sm bg-green-500" />60-79%</span>
            <span className="flex items-center gap-1"><span className="w-3 h-3 rounded-sm bg-yellow-500" />40-59%</span>
            <span className="flex items-center gap-1"><span className="w-3 h-3 rounded-sm bg-orange-500" />20-39%</span>
            <span className="flex items-center gap-1"><span className="w-3 h-3 rounded-sm bg-red-500" />1-19%</span>
            <span className="flex items-center gap-1"><span className="w-3 h-3 rounded-sm bg-muted" />0%</span>
          </div>
        </div>
      )}

      {/* Revenue Curves */}
      {view === 'revenue' && (
        <div className="space-y-4">
          <div className="bg-card border border-border rounded-sm p-5" data-testid="revenue-curves-chart">
            <div className="text-sm font-semibold tracking-tight mb-1">Cumulative Revenue by Cohort</div>
            <div className="text-[10px] text-muted-foreground mb-4">How each cohort&apos;s total revenue grows over time (first 12 months)</div>
            <ResponsiveContainer width="100%" height={350}>
              <LineChart data={revenueChartData}>
                <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                <XAxis dataKey="period" stroke="hsl(var(--foreground))" style={{ fontSize: '10px' }} />
                <YAxis stroke="hsl(var(--foreground))" style={{ fontSize: '10px' }} tickFormatter={(v) => `₹${(v / 100000).toFixed(1)}L`} />
                <Tooltip
                  formatter={(v, name) => [fmtCurrency(v), fmtMonth(name)]}
                  contentStyle={{ backgroundColor: 'hsl(var(--card))', border: '1px solid hsl(var(--border))', borderRadius: '0.25rem', fontSize: '11px' }}
                />
                <Legend formatter={(v) => fmtMonth(v)} wrapperStyle={{ fontSize: '10px' }} />
                {[...selectedCohorts].map((cohort, i) => (
                  <Line key={cohort} type="monotone" dataKey={cohort} stroke={COHORT_COLORS[i % COHORT_COLORS.length]} strokeWidth={2} dot={false} />
                ))}
              </LineChart>
            </ResponsiveContainer>
          </div>

          {/* Cohort selector */}
          <div className="bg-card border border-border rounded-sm p-4" data-testid="cohort-selector">
            <div className="text-xs font-semibold text-muted-foreground mb-2">Select cohorts to compare (click to toggle):</div>
            <div className="flex flex-wrap gap-1.5">
              {(data.cohorts || []).map((cohort, i) => {
                const isSelected = selectedCohorts.has(cohort);
                const size = retention_matrix.find(r => r.cohort === cohort)?.size || 0;
                return (
                  <button key={cohort} onClick={() => toggleCohort(cohort)}
                    className={`px-2 py-1 text-[10px] rounded-sm font-medium transition-all border ${isSelected ? 'border-primary bg-primary/10 text-foreground' : 'border-border bg-background text-muted-foreground hover:border-primary/50'}`}
                    data-testid={`cohort-toggle-${i}`}
                    style={isSelected ? { borderColor: COHORT_COLORS[i % COHORT_COLORS.length] } : {}}>
                    {fmtMonth(cohort)} ({size})
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CohortAnalysis;
